// Copyright ©️ 2026 eVolve MEP, LLC

/**
 * Recovers an element's centerline — the line a conduit's spacing is measured from.
 *
 * Read bottom-up: {@link findElementCenterline} is the entry point, and it tries two sources.
 * First it tries line geometry, then the openings of the solid mesh. The first approach is one read.
 * Everything above them is the machinery for the second approach.
 *
 * That second source treats a conduit as a circle swept along a path, and works from the fact that the
 * circle is still in the mesh, at each end where the sweep stops. One opening fixes the whole answer: its
 * center is a point on the centerline, and its plane's normal is the direction the conduit runs from there.
 * Firing a ray along that normal and catching where it leaves the solid gives one straight length — the
 * whole conduit for a straight run, and for a bend the leg that runs off that end, since the arc curves out
 * of the ray's way and the ray exits through the wall exactly where the leg stops being straight.
 */

import {
  MIN_MEASURABLE_LENGTH_FT,
  getFragmentLineSegments,
  getFragmentWorldBounds,
  getFragmentWorldTriangles,
  rayCrossesTriangleAtFt,
  type WorldTriangle,
} from 'components/Autodesk/extensions/DimensioningExtension/RackDimensions/measurement/geometryHelpers';
import { type AutodeskFragmentList, getElementFragmentIds } from 'components/Autodesk/extensions/viewerHelpers';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { THREE } from 'components/Autodesk/viewerThree';
import { isNil, isNotNil } from 'helpers/isNotNil';

import InstanceTree = Autodesk.Viewing.InstanceTree;

/** The shortest piece of a centerline that is a straight length rather than one chord of a Revit arc. */
const MIN_STRAIGHT_LENGTH_FT = 0.5;
/** How far 2 triangles' normals may point apart and still be read as one flat face of the mesh. */
const COPLANAR_TOLERANCE_DEGREES = 1;
/** The fewest vertices a rim may hold and still be a tessellated circle rather than a polygon profile. */
const MIN_CIRCLE_SEGMENTS = 8;
/** How far inside the widest rim vertex a rim's outer ring reaches. Separates the 2 rings of a washer. */
const OUTER_RING_BAND = 0.05;
/** How far a rim vertex may sit off its ring's mean radius before the ring is not a circle. */
const CIRCLE_ROUNDNESS_TOLERANCE = 0.05;
/** How many times an opening's radius may differ from the conduit's own before it belongs to something else. */
const OPENING_RADIUS_TOLERANCE = 2;
/** Where a ray starts looking for its exit, as a share of the opening's radius, so it clears its own cap. */
const RAY_START_OFFSET = 0.01;
/** The shortest a traced leg may be, as a multiple of its own radius. Below that it is a rim, not a run. */
const MIN_LENGTH_TO_RADIUS = 2;
/** 2 traced legs whose ends agree this closely are one length, found once from each of its openings. */
const DUPLICATE_END_TOLERANCE_FT = 0.02;

/** One straight piece of an element's centerline, in world space. */
type CenterlineSegment = { start: THREE.Vector3; end: THREE.Vector3 };

/** Comparison method to check for short segments, which tend to be arcs within a bend. */
const centerlineSegmentLengthSufficient = ({ start, end }: CenterlineSegment) =>
  start.distanceTo(end) >= MIN_STRAIGHT_LENGTH_FT;

// region the flat faces of a mesh
/** A maximal patch of coplanar, edge-connected triangles, described by the vertices that bound it. */
type FlatFace = { normal: THREE.Vector3; rim: THREE.Vector3[] };

const MIN_COPLANAR_DOT = Math.cos((COPLANAR_TOLERANCE_DEGREES * Math.PI) / 180);

/** Type alias for a three.js vector component */
type VectorComponent = number;

/** A string representation of a vertex */
type PointKey = `${VectorComponent},${VectorComponent},${VectorComponent}`;

/**
 * A vertex reduced to a map key.
 *
 * Keying on the position rather than the buffer index is what makes the face walk below work on an unwelded
 * mesh, where one corner of the model is several vertices carrying identical coordinates. Exact equality is
 * safe for the same reason: those coordinates are the same bits, and the same world matrix is applied to
 * every one of them.
 */
const pointKey = ({ x, y, z }: THREE.Vector3): PointKey => `${x},${y},${z}`;

/** The key of the edge between 2 vertices — the same key from either end. */
type EdgeKey = `${PointKey}/${PointKey}`;

/** Converts a start and end vector into an {@link EdgeKey} */
const edgeKey = (start: THREE.Vector3, end: THREE.Vector3): EdgeKey => {
  // This will always have a length of two, but TypeScript forgets that when we call `toSorted`
  const [startKey, endKey] = [pointKey(start), pointKey(end)].toSorted() as [PointKey, PointKey];
  return `${startKey}/${endKey}`;
};

/** The 3 edges of a triangle, each as the pair of vertices it runs between. */
const triangleEdges = ({ a, b, c }: WorldTriangle): [THREE.Vector3, THREE.Vector3][] => [
  [a, b],
  [b, c],
  [c, a],
];

/** The key of the triangle formed by 3 vertices — the same key from any face. */
type TriangleKey = `${EdgeKey},${EdgeKey},${EdgeKey}`;

/** Converts a world triangle to a {@link TriangleKey} that identifies it. */
const triangleKey = (triangle: WorldTriangle): TriangleKey => {
  // Similar story to `edgeKey`, this will always be a list of length 3, but TypeScript forgets that when we call `toSorted`
  const [startKey, midKey, endKey] = triangleEdges(triangle)
    .map(([start, end]) => edgeKey(start, end))
    .toSorted() as [EdgeKey, EdgeKey, EdgeKey];
  return `${startKey},${midKey},${endKey}`;
};

/** A triangle's unit normal. Every {@link WorldTriangle} encloses an area, so every one has one. */
const triangleNormal = ({ a, b, c }: WorldTriangle): THREE.Vector3 =>
  b.clone().sub(a).cross(c.clone().sub(a)).normalize();

/** The distinct vertices among a set of them. */
const uniquePoints = (points: THREE.Vector3[]): THREE.Vector3[] => [
  ...new Map(points.map((point) => [pointKey(point), point])).values(),
];

/** Every triangle of a mesh that shares an edge with the given one, whichever face each belongs to. */
const buildEdgeNeighbors = (triangles: WorldTriangle[]): Map<EdgeKey, number[]> => {
  const neighbors = new Map<EdgeKey, number[]>();
  triangles.forEach((triangle, index) =>
    triangleEdges(triangle).forEach(([start, end]) => {
      const key = edgeKey(start, end);
      neighbors.set(key, [...(neighbors.get(key) ?? []), index]);
    }),
  );
  return neighbors;
};

/**
 * Every flat face of a mesh: patches of coplanar triangles grown out from a seed across shared edges.
 *
 * Each candidate is compared against the *seed's* normal rather than its neighbor's, which is what stops a
 * patch creeping the whole way around a tessellated tube wall one small step at a time. A curved wall
 * therefore comes back as a face per facet, which no rim below mistakes for a circle.
 */
const findFlatFaces = (triangles: WorldTriangle[]): FlatFace[] => {
  const triangleData = triangles.map(
    (triangle) => [triangle, triangleNormal(triangle), triangleKey(triangle)] as const,
  );
  const edgeNeighbors = buildEdgeNeighbors(triangles);
  const claimed = new Set<TriangleKey>();
  const flatFaces: FlatFace[] = [];

  for (const [currentTriangle, normal, currentKey] of triangleData) {
    if (claimed.has(currentKey)) continue;
    claimed.add(currentKey);

    /** Every edge the face touches, against how many of the face's own triangles use it. */
    const faceEdges = new Map<EdgeKey, { uses: number; ends: [THREE.Vector3, THREE.Vector3] }>();
    const pending = [currentTriangle];

    while (pending.length > 0) {
      const triangle = pending.pop();
      if (isNil(triangle)) continue;

      for (const ends of triangleEdges(triangle)) {
        const currentEdgeKey = edgeKey(...ends);
        faceEdges.set(currentEdgeKey, { uses: (faceEdges.get(currentEdgeKey)?.uses ?? 0) + 1, ends });

        for (const neighborIndex of edgeNeighbors.get(currentEdgeKey) ?? []) {
          const neighborData = triangleData[neighborIndex];
          if (isNil(neighborData)) continue;
          const [neighborTri, neighborNormal, neighborKey] = neighborData;
          if (claimed.has(neighborKey) || neighborNormal.dot(normal) < MIN_COPLANAR_DOT) continue;
          claimed.add(neighborKey);
          pending.push(neighborTri);
        }
      }
    }

    // An edge only 1 of the face's triangles uses has nothing on its far side: it is part of the boundary.
    const rim = [...faceEdges.values()].filter(({ uses }) => uses === 1).flatMap(({ ends }) => ends);
    flatFaces.push({ normal, rim: uniquePoints(rim) });
  }
  return flatFaces;
};
// endregion the flat faces of a mesh

// region the circular openings of a tube
/** An open end of a tube: a point on the centerline, and the direction the conduit runs inwards from it. */
type TubeOpening = { center: THREE.Vector3; direction: THREE.Vector3; radiusFt: number };

/** The average of a set of points, which for a whole ring of them is its center. */
const averageOf = (points: THREE.Vector3[]): THREE.Vector3 =>
  points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).divideScalar(Math.max(points.length, 1));

/**
 * The circle a flat face's rim describes, or `null` when it describes something else.
 *
 * Only the outermost ring of the rim is fitted, because a rim is not always one ring: a conduit drawn with
 * a wall thickness ends in a washer rather than a disc, and a disc triangulated as a fan keeps a vertex at
 * its middle. Both leave the outer ring as the only part that is a circle, and neither moves it off the
 * conduit's axis.
 */
const fitOpeningCircle = (rim: THREE.Vector3[]): { center: THREE.Vector3; radiusFt: number } | null => {
  if (rim.length < MIN_CIRCLE_SEGMENTS) return null;

  const rimCenter = averageOf(rim);
  const widestFt = Math.max(...rim.map((point) => point.distanceTo(rimCenter)));
  const outerRing = rim.filter((point) => point.distanceTo(rimCenter) >= widestFt * (1 - OUTER_RING_BAND));
  if (outerRing.length < MIN_CIRCLE_SEGMENTS) return null;

  const center = averageOf(outerRing);
  const radiiFt = outerRing.map((point) => point.distanceTo(center));
  const radiusFt = radiiFt.reduce((sum, radius) => sum + radius, 0) / radiiFt.length;
  if (radiusFt < MIN_MEASURABLE_LENGTH_FT) return null;

  const isRound = radiiFt.every((radius) => Math.abs(radius - radiusFt) <= radiusFt * CIRCLE_ROUNDNESS_TOLERANCE);
  return isRound ? { center, radiusFt } : null;
};

/** True when an opening is the size of the conduit it was found on, or when the conduit's size is unknown. */
const openingMatchesConduit = (radiusFt: number, conduitRadiusFt: number | null): boolean =>
  isNil(conduitRadiusFt) ||
  (radiusFt <= conduitRadiusFt * OPENING_RADIUS_TOLERANCE && radiusFt * OPENING_RADIUS_TOLERANCE >= conduitRadiusFt);

/**
 * Every circular opening of a mesh, each aimed inwards along the conduit.
 *
 * An opening's plane cuts the conduit square, so the plane's normal is the direction the conduit runs.
 * Which of the 2 ways along it points *into* the element is settled by the element's own center, rather
 * than by the winding of the mesh.
 */
const findTubeOpenings = (
  triangles: WorldTriangle[],
  geometryCenter: THREE.Vector3,
  conduitRadiusFt: number | null,
): TubeOpening[] =>
  findFlatFaces(triangles)
    .map(({ normal, rim }) => {
      const circle = fitOpeningCircle(rim);
      if (isNil(circle) || !openingMatchesConduit(circle.radiusFt, conduitRadiusFt)) return null;

      const inwards = geometryCenter.clone().sub(circle.center);
      return { ...circle, direction: normal.dot(inwards) >= 0 ? normal.clone() : normal.clone().negate() };
    })
    .filter(isNotNil);
// endregion the circular openings of a tube

// region tracing a centerline by ray
/**
 * Where a ray fired inwards from an opening leaves the element, as a distance along it, or `null` when it
 * never does.
 *
 * Two things stop it and the nearer wins. The mesh itself is one: a conduit drawn solid has its centerline
 * buried in material, so the first triangle the ray reaches is the far end of the straight run. The other
 * openings are the second, and are the only stop a conduit drawn with a hollow bore offers, since there the
 * ray runs down empty space and out through the hole at the far end without touching a triangle.
 */
const findRayExitFt = (
  triangles: WorldTriangle[],
  openings: TubeOpening[],
  { center, direction, radiusFt }: TubeOpening,
): number | null => {
  const alongConduit = new THREE.Ray(center, direction);
  const meshExitsFt = triangles.map((triangle) => rayCrossesTriangleAtFt(alongConduit, triangle));

  const openingExitsFt = openings.map(({ center: other }) => {
    const offset = other.clone().sub(center);
    const alongFt = offset.dot(direction);
    // Read before `sub`, which consumes `offset`.
    const asideFt = offset.sub(direction.clone().multiplyScalar(alongFt)).length();
    return asideFt <= radiusFt ? alongFt : null;
  });

  // The ray starts on its own opening, which both of the above duly report at a distance of zero.
  const exitsFt = [...meshExitsFt, ...openingExitsFt]
    .filter(isNotNil)
    .filter((distanceFt) => distanceFt > radiusFt * RAY_START_OFFSET);

  return exitsFt.length === 0 ? null : Math.min(...exitsFt);
};

/**
 * The straight length of conduit running inwards from one opening, or `null` when nothing usable runs from
 * it. A leg shorter than the conduit is wide is a rim of some fitting rather than a run of conduit.
 */
const traceCenterlineFromOpening = (
  triangles: WorldTriangle[],
  openings: TubeOpening[],
  opening: TubeOpening,
): CenterlineSegment | null => {
  const lengthFt = findRayExitFt(triangles, openings, opening);
  if (isNil(lengthFt) || lengthFt < opening.radiusFt * MIN_LENGTH_TO_RADIUS) return null;

  return {
    start: opening.center.clone(),
    end: opening.center.clone().add(opening.direction.clone().multiplyScalar(lengthFt)),
  };
};

/** True when 2 traced legs are one length of conduit, found once from each of the openings that bound it. */
const isSameCenterline = (one: CenterlineSegment, other: CenterlineSegment): boolean => {
  const meets = (point: THREE.Vector3, counterpart: THREE.Vector3) =>
    point.distanceTo(counterpart) <= DUPLICATE_END_TOLERANCE_FT;

  return (
    (meets(one.start, other.start) && meets(one.end, other.end)) ||
    (meets(one.start, other.end) && meets(one.end, other.start))
  );
};

/**
 * Every straight length of conduit in one solid fragment — one traced from each opening, since an opening
 * is where a straight run begins. A straight conduit finds the same length from both of its ends and the
 * duplicate is dropped; a bend finds a different leg from each, which is the point.
 */
const findTubeCenterlines = (
  fragList: AutodeskFragmentList,
  fragId: number,
  conduitRadiusFt: number | null,
): CenterlineSegment[] => {
  const triangles = getFragmentWorldTriangles(fragList, fragId);
  if (triangles.length === 0) return [];

  const geometryCenter = getFragmentWorldBounds(fragList, fragId).getCenter(new THREE.Vector3());
  const openings = findTubeOpenings(triangles, geometryCenter, conduitRadiusFt);

  return openings
    .map((opening) => traceCenterlineFromOpening(triangles, openings, opening))
    .filter(isNotNil)
    .reduce<CenterlineSegment[]>(
      (kept, centerline) => (kept.some((other) => isSameCenterline(other, centerline)) ? kept : [...kept, centerline]),
      [],
    );
};

const findElementTubeCenterlines = (
  instanceTree: InstanceTree,
  fragList: AutodeskFragmentList,
  dbId: AutodeskDbId,
  /** The conduit's own radius, used to verify a centerline recovered from a mesh. */
  conduitRadiusFt: number | null,
) =>
  getElementFragmentIds(instanceTree, dbId).flatMap((fragId) => findTubeCenterlines(fragList, fragId, conduitRadiusFt));
// endregion tracing a centerline by ray

/**
 * Every straight length of an element's centerline. Empty for a support — a strut, trapeze, or end box —
 * which has no centerline and no tube-shaped geometry.
 */
export const findElementCenterline = (
  instanceTree: Autodesk.Viewing.InstanceTree,
  fragList: AutodeskFragmentList,
  dbId: AutodeskDbId,
  /** The conduit's own radius, used to verify a centerline recovered from a mesh. */
  conduitRadiusFt: number | null,
): CenterlineSegment[] => {
  const fragmentIds = getElementFragmentIds(instanceTree, dbId);

  // Prefer line geometry first.
  const lineSegments: CenterlineSegment[] = fragmentIds
    .flatMap((fragmentId) => getFragmentLineSegments(fragList, fragmentId))
    .filter(centerlineSegmentLengthSufficient);

  if (lineSegments.length > 0) return lineSegments;

  // Failing that, trace the openings of the solid mesh.
  return findElementTubeCenterlines(instanceTree, fragList, dbId, conduitRadiusFt);
};
